import { useEffect, useState } from 'react';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    RowSelectionState,
    useReactTable,
} from '@tanstack/react-table';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useTranslation } from 'react-i18next';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import type { IntegrationMessageListItemDto } from './integration-message.types';

type Props = {
    columns: ColumnDef<IntegrationMessageListItemDto>[];
    data?: { items?: IntegrationMessageListItemDto[]; totalCount: number };
    page: number;
    onPageChange: (page: number) => void;
    paginationStep?: number;
    isLoadingPage?: boolean;
    onRowClick?: (row: IntegrationMessageListItemDto) => void;
    onSelectedIdsChange?: (ids: string[]) => void;
};

const IntegrationMessagesTable = ({
    columns,
    data,
    page,
    onPageChange,
    paginationStep = 10,
    isLoadingPage,
    onRowClick,
    onSelectedIdsChange,
}: Props) => {
    const { t } = useTranslation();
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const table = useReactTable({
        data: data?.items ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        getRowId: (row) => row.id,
        state: { rowSelection },
    });

    useEffect(() => {
        setRowSelection({});
    }, [data?.items, page]);

    useEffect(() => {
        const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
        onSelectedIdsChange?.(selectedIds);
    }, [rowSelection, table, onSelectedIdsChange]);

    return (
        <>
            <div className="rounded-semi-xl border border-popover-100 bg-popover-10">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext(),
                                              )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoadingPage ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-6 text-center text-sm">
                                    {t('elements.table.body.loading')}
                                </TableCell>
                            </TableRow>
                        ) : data?.totalCount && data.totalCount > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className={onRowClick ? 'cursor-pointer select-none' : 'select-none'}
                                    onClick={() => onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="py-6 text-center text-sm font-medium">
                                    {t('elements.table.body.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {!!data?.totalCount && data.totalCount > paginationStep && (
                <ETablePaginationWithApi
                    totalCount={Math.ceil(data.totalCount / paginationStep)}
                    page={page}
                    setPage={onPageChange}
                />
            )}
        </>
    );
};

export default IntegrationMessagesTable;
